import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';

import { SR_API_ACTION_INVENTORY_SCAN_GEOLOCATION_ERROR } from 'api/actionTypes';

import {
  createInitialValues,
  createFormValidationFn,
  showFieldIf,
} from 'client/helpers/formikHelpers';

import {
  INVENTORY_ITEM_STATUSES_OPTIONS,
  INVENTORY_ITEM_TYPE_KWIKSET_LOCK,
  INVENTORY_ITEM_TYPE_RASPBERRY_PI,
  INVENTORY_ITEM_TYPE_ZTE_STICK,
} from 'common/inventoryConstants';

import useDispatch from 'client/hooks/useDispatch';
import postWithState from 'client/actions/postWithState';

import ButtonDeprecated, { BUTTON_THEME_BLUE_GREYCLIFF } from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrFormikErrorMessage from 'client/components/SrFormikErrorMessage';
import SrBarcodeScanner from 'client/components/SrBarcodeScanner';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import './SrSunSetupDriverJobPage.less';

const DRIVER_SUNLOCK_STATE_INITIAL = 'initial';
const DRIVER_SUNLOCK_STATE_SERIAL_NUMBER_SET = 'serialNumberSet';

const getFormFields = ({ setIsBarcodeScannerOpen, setFocusedField, isSunlockTakedown }) => {
  return [
    {
      id: 'raspberryPiSerialNumber',
      title: 'Raspberry Pi Barcode Number',
      type: 'barcode_scan',
      properties: {
        placeholder: 'Write barcode number here...',
        onScanClick: () => {
          setIsBarcodeScannerOpen(true);
          setFocusedField('raspberryPiSerialNumber');
        },
      },
    },
    showFieldIf(isSunlockTakedown, {
      id: 'raspberryPiStatus',
      title: 'Raspberry Pi Status',
      type: 'select',
      properties: {
        placeholder: 'Not selected',
        choices: INVENTORY_ITEM_STATUSES_OPTIONS,
      },
    }),
    {
      id: 'zteStickSerialNumber',
      title: 'ZTE Stick SIM Barcode Number',
      type: 'barcode_scan',
      properties: {
        placeholder: 'Write barcode number here...',
        onScanClick: () => {
          setIsBarcodeScannerOpen(true);
          setFocusedField('zteStickSerialNumber');
        },
      },
    },
    showFieldIf(isSunlockTakedown, {
      id: 'zteStickStatus',
      title: 'ZTE Stick SIM Status',
      type: 'select',
      properties: {
        placeholder: 'Not selected',
        choices: INVENTORY_ITEM_STATUSES_OPTIONS,
      },
    }),
    {
      id: 'kwiksetLockSerialNumber',
      title: 'Kwikset Lock Barcode Number',
      type: 'barcode_scan',
      properties: {
        placeholder: 'Write barcode number here...',
        onScanClick: () => {
          setIsBarcodeScannerOpen(true);
          setFocusedField('kwiksetLockSerialNumber');
        },
      },
    },
    showFieldIf(isSunlockTakedown, {
      id: 'kwiksetLockStatus',
      title: 'Kwikset Lock Status',
      type: 'select',
      properties: {
        placeholder: 'Not selected',
        choices: INVENTORY_ITEM_STATUSES_OPTIONS,
      },
    }),
  ];
};

const SrSunSetupDriverJobSunlockBarcode = ({
  sunlock,
  itemScans,
  listingAssignedItems,
  isSunlockTakedown,
  formikBag,
  handleSunlockRegistration,
  getCurrentLocation,
  requestGeolocationPermission,
}) => {
  const [viewState, setViewState] = useState(
    sunlock || itemScans.length > 0
      ? DRIVER_SUNLOCK_STATE_SERIAL_NUMBER_SET
      : DRIVER_SUNLOCK_STATE_INITIAL,
  );
  const [sunlockMissingError, setSunlockMissingError] = useState(false);
  const [isBarcodeScannerOpen, setIsBarcodeScannerOpen] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);

  const dispatch = useDispatch();

  const _onSunlock = (sunlock) => {
    setSunlockMissingError(!sunlock);
    setViewState(
      sunlock || itemScans.length > 0
        ? DRIVER_SUNLOCK_STATE_SERIAL_NUMBER_SET
        : DRIVER_SUNLOCK_STATE_INITIAL,
    );
  };

  const _onSubmit = async (formValues) => {
    const location = await _requestGeolocationPermission();
    const { latitude, longitude } = location;

    const {
      raspberryPiSerialNumber,
      raspberryPiStatus,
      zteStickSerialNumber,
      zteStickStatus,
      kwiksetLockSerialNumber,
      kwiksetLockStatus,
    } = formValues;

    const itemGroup = [
      {
        type: INVENTORY_ITEM_TYPE_RASPBERRY_PI,
        serialNumber: raspberryPiSerialNumber,
        ...(isSunlockTakedown && { status: raspberryPiStatus }),
      },
      {
        type: INVENTORY_ITEM_TYPE_ZTE_STICK,
        serialNumber: zteStickSerialNumber,
        ...(isSunlockTakedown && { status: zteStickStatus }),
      },
      {
        type: INVENTORY_ITEM_TYPE_KWIKSET_LOCK,
        serialNumber: kwiksetLockSerialNumber,
        ...(isSunlockTakedown && { status: kwiksetLockStatus }),
      },
    ];

    handleSunlockRegistration({
      itemGroup,
      latitude,
      longitude,
    }).then((sunlock) => {
      return _onSunlock(sunlock);
    });
  };

  const _createGeolocationError = ({ errorMessage }) => {
    dispatch(postWithState, {
      action: SR_API_ACTION_INVENTORY_SCAN_GEOLOCATION_ERROR,
      payload: {
        errorMessage,
      },
    });
  };

  const _requestGeolocationPermission = async () => {
    setIsLocationLoading(true);
    try {
      await requestGeolocationPermission();
      const location = await getCurrentLocation();

      setIsLocationLoading(false);
      return location;
    } catch (error) {
      _createGeolocationError({
        errorMessage: error.message,
      });
      setIsLocationLoading(false);
      throw error;
    }
  };

  const _onFieldScan = ({ barcode, formikBag }) => {
    if (!focusedField) {
      return;
    }
    formikBag.setFieldValue(focusedField, barcode);
    setFocusedField(null);
    setIsBarcodeScannerOpen(false);
  };

  const _handleScannerClose = () => {
    setIsBarcodeScannerOpen(false);
    setFocusedField(null);
  };

  const getItemScansSerialNumberMapping = () => {
    const itemScansSerialNumberMapping = {};
    itemScans.forEach((itemScan) => {
      itemScansSerialNumberMapping[itemScan.inventoryItemTypeId] = {
        serialNumber: itemScan.serialNumber,
        status: itemScan.status,
      };
    });
    return itemScansSerialNumberMapping;
  };

  const itemScansSerialNumberMapping = getItemScansSerialNumberMapping();
  const initialValues = {
    raspberryPiSerialNumber:
      sunlock?.raspberryPiSerialNumber ||
      listingAssignedItems[INVENTORY_ITEM_TYPE_RASPBERRY_PI]?.serialNumber ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_RASPBERRY_PI]?.serialNumber ||
      null,
    raspberryPiStatus:
      listingAssignedItems[INVENTORY_ITEM_TYPE_RASPBERRY_PI]?.status ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_RASPBERRY_PI]?.status ||
      null,
    zteStickSerialNumber:
      listingAssignedItems[INVENTORY_ITEM_TYPE_ZTE_STICK]?.serialNumber ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_ZTE_STICK]?.serialNumber ||
      null,
    zteStickStatus:
      listingAssignedItems[INVENTORY_ITEM_TYPE_ZTE_STICK]?.status ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_ZTE_STICK]?.status ||
      null,
    kwiksetLockSerialNumber:
      listingAssignedItems[INVENTORY_ITEM_TYPE_KWIKSET_LOCK]?.serialNumber ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_KWIKSET_LOCK]?.serialNumber ||
      null,
    kwiksetLockStatus:
      listingAssignedItems[INVENTORY_ITEM_TYPE_KWIKSET_LOCK]?.status ||
      itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_KWIKSET_LOCK]?.status ||
      null,
  };

  return (
    <div styleName="card" className="fieldsContainer" id="field-sunlockId">
      <span styleName="cardTitle">Register Sunlock</span>

      <SrBox margin="16px 0 0">
        {viewState === DRIVER_SUNLOCK_STATE_SERIAL_NUMBER_SET && (
          <>
            <SrBox styleName="jobDetailsBox" padding="16px 0 16px">
              <SrBox marginBottom="16px">
                <SrBox styleName="title">Raspberry Pi Barcode Number</SrBox>
                {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_RASPBERRY_PI]?.status && (
                  <SrBox styleName="status">
                    Status:&nbsp;
                    {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_RASPBERRY_PI]?.status}
                  </SrBox>
                )}
                <SrBox styleName="serialNumber">
                  {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_RASPBERRY_PI]?.serialNumber ||
                    sunlock?.raspberryPiSerialNumber ||
                    '--'}
                </SrBox>
              </SrBox>
              <SrBox borderTop="1px solid #E0E0E0" paddingTop="8px" marginBottom="16px">
                <SrBox styleName="title">ZTE Stick SIM Barcode Number</SrBox>
                {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_ZTE_STICK]?.status && (
                  <SrBox styleName="status">
                    Status:&nbsp;
                    {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_ZTE_STICK]?.status}
                  </SrBox>
                )}
                <SrBox styleName="serialNumber">
                  {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_ZTE_STICK]?.serialNumber ||
                    '--'}
                </SrBox>
              </SrBox>
              <SrBox borderTop="1px solid #E0E0E0" paddingTop="8px">
                <SrBox styleName="title">Kwikset Lock Barcode Number</SrBox>
                {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_KWIKSET_LOCK]?.status && (
                  <SrBox styleName="status">
                    Status:&nbsp;
                    {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_KWIKSET_LOCK]?.status}
                  </SrBox>
                )}
                <SrBox styleName="serialNumber">
                  {itemScansSerialNumberMapping[INVENTORY_ITEM_TYPE_KWIKSET_LOCK]?.serialNumber ||
                    '--'}
                </SrBox>
              </SrBox>
            </SrBox>
            <SrBox margin="16px 0 0">
              <ButtonDeprecated
                theme={BUTTON_THEME_BLUE_GREYCLIFF}
                onClick={() => setViewState(DRIVER_SUNLOCK_STATE_INITIAL)}
              >
                Scan Again
              </ButtonDeprecated>
            </SrBox>
          </>
        )}
        {viewState === DRIVER_SUNLOCK_STATE_INITIAL && (
          <Formik
            onSubmit={_onSubmit}
            initialValues={createInitialValues({
              formFields: getFormFields({
                setIsBarcodeScannerOpen,
                setFocusedField,
                isSunlockTakedown,
              }),
              defaultValues: initialValues,
            })}
            validate={() =>
              createFormValidationFn(
                getFormFields({
                  setIsBarcodeScannerOpen,
                  setFocusedField,
                  isSunlockTakedown,
                }),
              )
            }
            render={(formikBag) => {
              const formFields = getFormFields({
                setFocusedField,
                setIsBarcodeScannerOpen,
                isSunlockTakedown,
              });
              return (
                <Form>
                  {formFields.map((formField) => (
                    <>
                      <SrFormikField
                        formikBag={formikBag}
                        formField={formField}
                        theme={SR_FIELD_THEME_ADOBE}
                        key={formField.id}
                      />
                      {sunlockMissingError && formField.id === 'raspberryPiSerialNumber' && (
                        <SrBox padding="0 0 16px">
                          <SrErrorMessage>
                            A Sunlock with this Raspberry Pi Barcode Number was not found. Please
                            try again.
                          </SrErrorMessage>
                        </SrBox>
                      )}
                    </>
                  ))}
                  {isBarcodeScannerOpen && (
                    <SrBarcodeScanner
                      onText={(barcode) => _onFieldScan({ barcode, formikBag })}
                      onClose={_handleScannerClose}
                    />
                  )}
                  <ButtonDeprecated
                    margin="24px 0 0"
                    type="submit"
                    theme={BUTTON_THEME_BLUE_GREYCLIFF}
                    disabled={isLocationLoading}
                  >
                    {isLocationLoading ? 'Loading...' : 'Submit'}
                  </ButtonDeprecated>
                </Form>
              );
            }}
          />
        )}
      </SrBox>
      <SrFormikErrorMessage id="sunlockId" formikBag={formikBag} />
    </div>
  );
};

SrSunSetupDriverJobSunlockBarcode.propTypes = {
  sunlock: PropTypes.object,
  itemScans: PropTypes.array.isRequired,
  listingAssignedItems: PropTypes.array.isRequired,
  isSunlockTakedown: PropTypes.bool.isRequired,
  handleSunlockRegistration: PropTypes.func.isRequired,
  formikBag: PropTypes.object.isRequired,
  getCurrentLocation: PropTypes.func.isRequired,
  requestGeolocationPermission: PropTypes.func.isRequired,
};

SrSunSetupDriverJobSunlockBarcode.displayName = 'SrSunSetupDriverJobSunlockBarcode';

export default SrSunSetupDriverJobSunlockBarcode;
